/**
 * Haptic Feedback Utilities for Mobile Devices
 * Provides tactile feedback for user interactions
 */

type HapticStyle = 'light' | 'medium' | 'heavy' | 'selection' | 'success' | 'warning' | 'error';

/**
 * Trigger haptic feedback if available on the device
 */
export const haptics = {
  /**
   * Light impact - for subtle interactions like hover
   */
  light: () => {
    triggerHaptic('light');
  },

  /**
   * Medium impact - for standard button presses
   */
  medium: () => {
    triggerHaptic('medium');
  },

  /**
   * Heavy impact - for important actions
   */
  heavy: () => {
    triggerHaptic('heavy');
  },

  /**
   * Selection - for picker/slider changes
   */
  selection: () => {
    triggerHaptic('selection');
  },

  /**
   * Success notification feedback
   */
  success: () => {
    triggerHaptic('success');
  },

  /**
   * Warning notification feedback
   */
  warning: () => {
    triggerHaptic('warning');
  },

  /**
   * Error notification feedback
   */
  error: () => {
    triggerHaptic('error');
  },

  /**
   * Custom vibration pattern (fallback for non-iOS devices)
   */
  pattern: (pattern: number[]) => {
    if (navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  },
};

/**
 * Internal function to trigger haptic feedback
 */
function triggerHaptic(style: HapticStyle) {
  // iOS Haptic Engine (via Webkit)
  if (window.webkit?.messageHandlers?.haptic) {
    window.webkit.messageHandlers.haptic.postMessage(style);
    return;
  }

  // Fallback to Vibration API for Android and other devices
  if (!navigator.vibrate) return;

  const patterns: Record<HapticStyle, number[]> = {
    light: [10],
    medium: [20],
    heavy: [30],
    selection: [5],
    success: [10, 50, 10],
    warning: [20, 50, 20],
    error: [30, 50, 30, 50, 30],
  };

  navigator.vibrate(patterns[style] || [10]);
}

/**
 * Check if haptic feedback is supported
 */
export function isHapticsSupported(): boolean {
  return !!(navigator.vibrate || window.webkit?.messageHandlers?.haptic);
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    webkit?: {
      messageHandlers?: {
        haptic?: {
          postMessage: (message: string) => void;
        };
      };
    };
  }
}
